import React, { useState } from 'react';
import Icon from '@mdi/react';
import { mdiContentDuplicate, mdiDeleteOutline, mdiSquareEditOutline, mdiArrowLeft, mdiArrowRight, mdiChevronDown, mdiChevronUp } from '@mdi/js';
import '../styles/TransactionsTable.css';
import useIsMobile from '../hooks/useIsMobile';

const TransactionsTable = ({
    transactions,
    totalPages,
    apiParams,
    onSort,
    onPaginate,
    onItemsPerPageChange,
    onReconciledChange,
    onEdit,
    onDuplicate,
    onDelete
}) => {
    const isMobile = useIsMobile();

    // Sum and average of the transactions currently displayed (current page) —
    // shown in the top pagination bar to fill the empty middle space.
    const stats = (() => {
        const sum = transactions.reduce((acc, t) => acc + (Number(t.amount) || 0), 0);
        const avg = transactions.length > 0 ? sum / transactions.length : 0;
        const fmt = (value) => value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
        return { sum: fmt(sum), avg: fmt(avg) };
    })();

    // Function to group transactions by date
    const groupTransactionsByDate = () => {
        const grouped = {};
        transactions.forEach(transaction => {
            if (!grouped[transaction.date]) {
                grouped[transaction.date] = [];
            }
            grouped[transaction.date].push(transaction);
        });
        return grouped;
    };

    const formatAmount = (amount) => {
        const isIncome = amount >= 0;
        const absAmount = Math.abs(amount).toLocaleString('en-US', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        });

        return (
            <>
                <strong>{isIncome ? '+ ' : '- '}</strong>
                {absAmount}
            </>
        );
    };

    const MobileTransactionItem = ({ transaction, index, date }) => {
        const [expanded, setExpanded] = useState(false);
        return (
            <li
                key={transaction.id}
                className={`transaction-item ${index === 0 ? 'first-of-date' : ''} ${expanded ? 'expanded' : ''}`}
                data-date={date}
            >
                <div
                    className="transaction-summary"
                    onClick={() => setExpanded(!expanded)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setExpanded(!expanded); } }}
                >
                    <span className="mobile-date">{transaction.date}</span>
                    <span className="mobile-subject">{transaction.subject_name}</span>
                    <span className={`mobile-amount ${transaction.amount >= 0 ? 'amount-income' : 'amount-expense'}`}>
                        {formatAmount(transaction.amount)}
                    </span>
                    <Icon path={expanded ? mdiChevronUp : mdiChevronDown} size={0.75} className="mobile-expand-chevron" />
                </div>
                {expanded && (
                    <div className="transaction-details">
                        <p><span className="detail-label">Account:</span> {transaction.account_name}</p>
                        <p><span className="detail-label">Category:</span> {transaction.category_name}</p>
                        <p><span className="detail-label">Balance:</span> {transaction.running_balance}</p>
                        <p><span className="detail-label">Reconciled:</span>
                            <label className="reconciled-checkbox">
                                <input
                                    type="checkbox"
                                    checked={transaction.is_reconciled}
                                    onChange={(e) => onReconciledChange(transaction.id, e.target.checked)}
                                />
                                <span className="custom-checkbox"></span>
                            </label>
                        </p>
                        {transaction.notes && (
                            <p><span className="detail-label">Notes:</span> {transaction.notes}</p>
                        )}
                        <div className="mobile-actions">
                            <button className="btn btn--ghost" onClick={() => onEdit(transaction)}>
                                <Icon path={mdiSquareEditOutline} size={0.75} />
                            </button>
                            <button className="btn btn--ghost" onClick={() => onDuplicate(transaction)}>
                                <Icon path={mdiContentDuplicate} size={0.75} />
                            </button>
                            <button className="btn btn--ghost" onClick={() => onDelete(transaction)}>
                                <Icon path={mdiDeleteOutline} size={0.75} />
                            </button>
                        </div>
                    </div>
                )}
            </li>
        );
    };

    const groupedTransactions = groupTransactionsByDate();
    const dates = Object.keys(groupedTransactions);

    return (
        <div className="table-wrapper">
            <div className="pagination-controls">
                <div className='itemsperpage-selector'>
                    <label>
                        <select
                            value={apiParams.per_page}
                            onChange={(e) => onItemsPerPageChange(Number(e.target.value))}
                        >
                            <option value={10}>10</option>
                            <option value={20}>20</option>
                            <option value={50}>50</option>
                            <option value={100}>100</option>
                            <option value={200}>200</option>
                        </select>
                    </label>
                </div>
                <div className="pagination-stats">
                    Sum: {stats.sum} - Avg: {stats.avg}
                </div>
                <div className='navigation-controls'>
                    <button
                        onClick={() => onPaginate(apiParams.page - 1)}
                        disabled={apiParams.page === 1}
                    >
                        <Icon path={mdiArrowLeft} size={0.75} />
                    </button>
                    <span>
                        Page {apiParams.page} of {totalPages}
                    </span>
                    <button
                        onClick={() => onPaginate(apiParams.page + 1)}
                        disabled={apiParams.page === totalPages}
                    >
                        <Icon path={mdiArrowRight} size={0.75} />
                    </button>
                </div>
            </div>

            {isMobile ? (
                <ul className="transaction-list">
                    {dates.map((date, dateIndex) => (
                        <React.Fragment key={date}>
                            {groupedTransactions[date].map((transaction, index) => (
                                <MobileTransactionItem
                                    key={transaction.id}
                                    transaction={transaction}
                                    index={index}
                                    date={date}
                                />
                            ))}
                        </React.Fragment>
                    ))}
                </ul>
            ) : (
                <table>
                    <thead>
                        <tr>
                            <th onClick={() => onSort('account')}>
                                Account {apiParams.sort === 'account' && (apiParams.dir === 'asc' ? '↑' : '↓')}
                            </th>
                            <th onClick={() => onSort('date')}>
                                Date {apiParams.sort === 'date' && (apiParams.dir === 'asc' ? '↑' : '↓')}
                            </th>
                            <th onClick={() => onSort('subject')}>
                                Subject {apiParams.sort === 'subject' && (apiParams.dir === 'asc' ? '↑' : '↓')}
                            </th>
                            <th onClick={() => onSort('amount')}>
                                Amount {apiParams.sort === 'amount' && (apiParams.dir === 'asc' ? '↑' : '↓')}
                            </th>
                            <th onClick={() => onSort('category')}>
                                Category {apiParams.sort === 'category' && (apiParams.dir === 'asc' ? '↑' : '↓')}
                            </th>
                            <th>Balance</th>
                            <th onClick={() => onSort('reconciled')}>
                                Reconciled {apiParams.sort === 'reconciled' && (apiParams.dir === 'asc' ? '↑' : '↓')}
                            </th>
                            <th>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {dates.map((date, dateIndex) => (
                            <React.Fragment key={date}>
                                {groupedTransactions[date].map((transaction, index) => (
                                    <tr key={transaction.id} className={index === 0 ? 'first-of-date' : ''} data-date={date}>
                                        <td>{transaction.account_name}</td>
                                        <td>{transaction.date}</td>
                                        <td>{transaction.subject_name}</td>
                                        <td className="num">{formatAmount(transaction.amount)}</td>
                                        <td>{transaction.category_name}</td>
                                        <td className="num">{transaction.running_balance}</td>
                                        <td>
                                            <label className="reconciled-checkbox">
                                                <input
                                                    type="checkbox"
                                                    checked={transaction.is_reconciled}
                                                    onChange={(e) => onReconciledChange(transaction.id, e.target.checked)}
                                                />
                                                <span className="custom-checkbox"></span>
                                            </label>
                                        </td>
                                        <td>
                                            <button
                                                className="btn btn--ghost"
                                                onClick={() => onEdit(transaction)}
                                            >
                                                <Icon path={mdiSquareEditOutline} size={0.75} />
                                            </button>
                                            <button
                                                className="btn btn--ghost"
                                                onClick={() => onDuplicate(transaction)}
                                            >
                                                <Icon path={mdiContentDuplicate} size={0.75} />
                                            </button>
                                            <button
                                                className="btn btn--ghost"
                                                onClick={() => onDelete(transaction)}
                                            >
                                                <Icon path={mdiDeleteOutline} size={0.75} />
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </React.Fragment>
                        ))}
                    </tbody>
                </table>
            )}
        </div>
    );
};

export default TransactionsTable;